import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { userApiClient } from "../../../../api/HttpClient";
import { getBookingDetails } from "../../../../api/services/guestService";
import LandingHeader from "../../../../components/landing/Header";

interface BookingDetails {
  check_in_date: string;
  check_out_date: string;
  number_of_guests: number;
  total_price: number;
  property_id: number;
}

interface PropertyDetails {
  name: string;
  accommodation_type: string;
  address: string;
}

const PaymentFailure: React.FC = () => {
  const { booking_id } = useParams<{ booking_id: string }>();
  const navigate = useNavigate();
  const [bookingDetails, setBookingDetails] = useState<BookingDetails | null>(null);
  const [propertyDetails, setPropertyDetails] = useState<PropertyDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const bookingResponse = await getBookingDetails(booking_id!);
        setBookingDetails({
          ...bookingResponse,
          total_price: parseFloat(bookingResponse.total_price),
        });

        if (bookingResponse.property_id) {
          const client = await userApiClient;
          const propertyResponse = await client.get<PropertyDetails>(`/api/hosts/properties/${bookingResponse.property_id}/`);
          setPropertyDetails({
            name: propertyResponse.name,
            accommodation_type: propertyResponse.accommodation_type,
            address: propertyResponse.address, // Assuming address is available
          });
        }
      } catch (err) {
        setError("Failed to fetch booking details.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [booking_id]);

  const handleTryAgain = () => {
    navigate(`/guest/booking-confirmation/${booking_id}`);
  };

  const handleGoToBookings = () => {
    navigate('/dashboard', { state: { showMyBookings: true } });
  };

  if (loading) {
    return (
      <div className="container mx-auto mt-8 text-center">
        <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="text-lg">Loading booking details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto mt-8 text-center">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      </div>
    );
  }

  if (!bookingDetails) {
    return (
      <div className="container mx-auto mt-8 text-center">
        <p className="text-lg font-semibold">No booking details found.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <LandingHeader />

    <main className="flex-grow container mx-auto py-12 px-4">
        <div className="max-w-2xl mx-auto bg-white p-8 md:p-12 rounded-2xl shadow-lg text-center">
            {/* Canceled Icon */}
            <div className="mx-auto bg-red-100 rounded-full h-20 w-20 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x text-red-600 h-12 w-12"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
            </div>

            {/* Confirmation Message */}
            <h1 className="text-3xl md:text-4xl font-bold text-black mt-6">Payment Failed</h1>
            <p className="text-gray-600 mt-2">There was an issue processing your payment.</p>
            
            {/* Refund Information (modified for failure) */}
            <p className="text-sm text-gray-500 mt-4">Please try again or contact support if the problem persists.</p>

            {/* Canceled Booking Details (modified for failure) */}
            {bookingDetails && (
              <div className="mt-8 text-left border rounded-lg p-6 space-y-4 bg-gray-50">
                  <h2 className="text-xl font-bold text-black">Booking Summary</h2>
                  <div>
                      <p className="text-sm text-gray-500">Booking ID</p>
                      <p className="font-bold text-black">{booking_id}</p>
                  </div>
                  {propertyDetails && (
                    <div>
                        <p className="text-sm text-gray-500">Property</p>
                        <p className="font-bold text-black">{propertyDetails.name}</p>
                        <p className="text-sm text-gray-600">{propertyDetails.address}</p>
                    </div>
                  )}
                  <div className="flex justify-between">
                      <div>
                          <p className="text-sm text-gray-500">Check-in</p>
                          <p className="font-bold text-black">{new Date(bookingDetails.check_in_date).toLocaleDateString()}</p>
                      </div>
                      <div>
                          <p className="text-sm text-gray-500">Check-out</p>
                          <p className="font-bold text-black">{new Date(bookingDetails.check_out_date).toLocaleDateString()}</p>
                      </div>
                  </div>
                  <div className="border-t pt-4 mt-4 flex justify-between items-center">
                      <p className="text-lg font-bold text-black">Amount Attempted</p>
                      <p className="text-lg font-bold text-red-600">₹{bookingDetails.total_price.toFixed(2)}</p>
                  </div>
              </div>
            )}

            {/* Actions */}
            <div className="mt-8 flex flex-col sm:flex-row justify-center items-center gap-4">
                <button
                  className="w-full sm:w-auto bg-deep-emerald-green text-white py-3 px-8 rounded-full hover:bg-emerald-700 font-bold text-lg transition-transform transform hover:scale-105"
                  onClick={handleTryAgain}
                >
                  Try Again
                </button>
                <button
                  className="w-full sm:w-auto font-bold text-deep-emerald-green"
                  onClick={handleGoToBookings}
                >
                  Go to My Bookings
                </button>
            </div>
        </div>
    </main>
    </div>
  );
};

export default PaymentFailure;
